import React, { useState } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { Dialog, EditorTextChangeEvent, FloatLabel, InputText, SCButton, SubmitHandler, Controller, DDCountries, DDBrands, DDMailTemplateType, Editor } from "../../Common/Imports";
import { ConfigMailTemplate_EXT } from '../../Models/Classes';
import { DecodeHtml } from '../../Common/Utility/DecodeHtml';

interface ConfigMailTemplatesNewProps {
    stakeholderId: number;
    OnSave?: () => void;
}

const ConfigMailTemplatesNew: React.FC<ConfigMailTemplatesNewProps> = ({ stakeholderId, OnSave }) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [Data, setData] = useState<ConfigMailTemplate_EXT>({
        idStakeholder: stakeholderId,
        provider: "",
        value: "",
        subject: ""
    });
    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue // Added setValue for controlling the form value
    } = useForm<ConfigMailTemplate_EXT>();
    const AxiosHelper = useAxios();

    const onSubmit: SubmitHandler<ConfigMailTemplate_EXT> = async (data: ConfigMailTemplate_EXT) => {
        data.value = DOMPurify.sanitize(data.value);
        try {
            data.idStakeholder = stakeholderId;
            await AxiosHelper.post(`StakeholderConfigs/MailTemplates/CreateConfig`, data);
            showToast('success', 'Success', t("Common.Toasts.New.Success"));
            if (OnSave) {
                OnSave();
            }
        } catch (error: unknown) {
            console.log(error);
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        }
    };

    const handleEditorChange = (e: EditorTextChangeEvent) => {
        const newValue = e.htmlValue ?? ""; // Ensure that newValue is never null, default to empty string
        setValue('value', newValue); // Update form value
        setData((prevData) => ({
            ...prevData, // Always return the previous data and update the value field
            value: newValue
        }));
    };

    const footerTemplate = () => (
        <div className="col d-flex justify-content-end">
            <SCButton Name="Save" Label={t("Common.Buttons.Save")} OnClick={handleSubmit(onSubmit)} />
        </div>
    );

    return (
        <Dialog
            maximized
            header="Edit Mail Template"
            visible={true}
            onHide={() => {
                if (OnSave) {
                    OnSave();
                }
            }}
            footer={footerTemplate}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
                <div className="row mb-3 mt-3">
                    <div className="col-md-6 mb-4">
                        <div className="row">
                            <Controller
                                name="idCountry"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <div className="col-md-4 mb-4">
                                        <FloatLabel>
                                            <DDCountries
                                                value={field.value}
                                                onChange={(value) => field.onChange(value)}
                                                onBlur={field.onBlur}
                                            />
                                            <label htmlFor="country">{t("Stakeholder.Configs_MailTemplate.Edit.Country")}</label>
                                        </FloatLabel>
                                        {fieldState.invalid && (
                                            <small className="p-error">
                                                {fieldState.error?.message}
                                            </small>
                                        )}
                                    </div>
                                )}
                            />
                            <Controller
                                name="idBrand"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <div className="col-md-4 mb-4">
                                        <FloatLabel>
                                            <DDBrands
                                                IdStakeholder={stakeholderId}
                                                value={field.value}
                                                onChange={(value) => field.onChange(value)}
                                                onBlur={field.onBlur}
                                            />
                                            <label htmlFor="country">{t("Stakeholder.Configs_MailTemplate.Edit.Brand")}</label>
                                        </FloatLabel>
                                        {fieldState.invalid && (
                                            <small className="p-error">
                                                {fieldState.error?.message}
                                            </small>
                                        )}
                                    </div>
                                )}
                            />
                            <Controller
                                name="type"
                                control={control}
                                rules={{ required: `${t("Stakeholder.Configs_MailTemplate.Edit.Type")} ${t("Common.Form.Required")}` }}
                                render={({ field, fieldState }) => (
                                    <div className="col-md-4 mb-4">
                                        <FloatLabel>
                                            <DDMailTemplateType
                                                IdStakeholder={stakeholderId}
                                                value={field.value}
                                                onChange={(value) => field.onChange(value)}
                                                onBlur={field.onBlur}
                                            />
                                            <label htmlFor="country">{t("Stakeholder.Configs_MailTemplate.Edit.Type")}</label>
                                        </FloatLabel>
                                        {fieldState.invalid && (
                                            <small className="p-error">
                                                {fieldState.error?.message}
                                            </small>
                                        )}
                                    </div>
                                )}
                            />
                        </div>
                        <div className="row">
                            <div className="col-md-12 mb-2">
                                <Controller
                                    name="provider"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <div className="col-md-12 mb-4">
                                            <FloatLabel>
                                                <InputText
                                                    value={field.value ?? ""}
                                                    onChange={(value) => field.onChange(value)}
                                                    onBlur={field.onBlur}
                                                />
                                                <label htmlFor="provider">{t("Stakeholder.Configs_MailTemplate.Edit.Provider")}</label>
                                            </FloatLabel>
                                            {fieldState.invalid && (
                                                <small className="p-error">
                                                    {fieldState.error?.message}
                                                </small>
                                            )}
                                        </div>
                                    )}
                                />
                                <Controller
                                    name="subject"
                                    control={control}
                                    rules={{ required: `${t("Stakeholder.Configs_MailTemplate.Edit.Subject")} ${t("Common.Form.Required")}` }}
                                    render={({ field, fieldState }) => (
                                        <div className="col-md-12 mb-4">
                                            <FloatLabel>
                                                <InputText
                                                    value={field.value}
                                                    onChange={(value) => field.onChange(value)}
                                                    onBlur={field.onBlur}
                                                />
                                                <label htmlFor="subject">{t("Stakeholder.Configs_MailTemplate.Edit.Subject")}</label>
                                            </FloatLabel>
                                            {fieldState.invalid && (
                                                <small className="p-error">
                                                    {fieldState.error?.message}
                                                </small>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12 mb-2">
                                <Controller
                                    name="value"
                                    control={control}
                                    rules={{ required: t("Stakeholder.Configs_MailTemplate.Edit.value") + " " + t("Auth.Register.Form.Required") }}
                                    render={({ field }) => (
                                        <Editor
                                            id="value"
                                            {...field}
                                            value={Data?.value ?? ""} // Bind Editor value to the Data state
                                            onTextChange={handleEditorChange} // Update Data and form value on change
                                            className={classNames({ 'p-invalid': errors.value })}
                                        />
                                    )}
                                />
                                {errors.value && (
                                    <small className="p-error">
                                        {errors.value.message}
                                    </small>
                                )}
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                NB: You can use the following placeholders to compose you email:
                                <ul>
                                    <li>
                                        [BrandName]
                                    </li>
                                    <li>
                                        [RefPaxName]
                                    </li>
                                    <li>
                                        [StructureName]
                                    </li>
                                    <li>
                                        [CheckInDate]
                                    </li>
                                    <li>
                                        [ProviderName]
                                    </li>
                                    <li>
                                        [ProviderCode]
                                    </li>
                                    <li>
                                        [BookingNumber]
                                    </li>
                                    <li>
                                        [CheckOutDate]
                                    </li>
                                    <li>
                                        [RoomName]
                                    </li>
                                    <li>
                                        [MealplanName]
                                    </li>
                                    <li>
                                        [PaxList]
                                    </li>
                                    <li>
                                        [CINMessage]
                                    </li>
                                    <li>
                                        [Error] - Only on Provider Mail Template
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 mb-4">
                        {parse(DecodeHtml(Data?.value ?? ""))}
                    </div>
                </div>
            </form>
        </Dialog >
    );
};

export default ConfigMailTemplatesNew;
